import { useAuth } from "@/_core/hooks/useAuth";
import ClarityLogoDark from "@/assets/clarity-logo.svg";
import ClarityLogoLight from "@/assets/clarity-logo-light.svg";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

import { useIsMobile } from "@/hooks/useMobile";
import { usePortalMode } from "@/contexts/PortalModeContext";
import { useDaylightModeOptional } from "@/contexts/DaylightModeContext";
import { getLoginUrl } from "@/lib/authRedirect";
import { getActiveMenuPath, isItemActiveInSet } from "@/lib/portalNav";
import { salesAmbienceUsesLightLogo } from "@shared/daylightMode";
import { hasRoleCapability, type UserRoleFields } from "@shared/userRoles";
import { CLUB_BOOKING_NAV_ITEM, isClubBookingNavActive, showClubBookingNav } from "@/components/clubBookingNav";
import { portalModuleAccessForUser } from "@shared/procurementInventory";
import { isHrsTesterEmail } from "@shared/hrsTesterAccess";
import { FloatingRoWorkTimers } from "@/components/roWorkTimer/FloatingRoWorkTimers";
import { PORTAL_MODULE_LABELS, PORTAL_MODULES, type PortalModule } from "@shared/portalModules";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  LogOut,
  PanelLeft,
  Users,
  ClipboardList,
  Package,
  Settings,
  Upload,
  UserCog,
  ScrollText,
  MapPin,
  CalendarCheck,
  CalendarDays,
  DollarSign,
  Bell,
  Copy,
  BarChart3,
  Truck,
  Activity,
  ChevronDown,
  ChevronRight,
  FileText,
  FileSpreadsheet,
  BarChart2,
  Kanban,
  BookOpen,
  Store,
  Wrench,
  TrendingUp,
  GitCompare,
  MessageSquareWarning,
  MessagesSquare,
  FolderOpen,
  FlaskConical,
  History,
  IdCard,
  Wallet,
  SlidersHorizontal,
  Check,
  UserRoundSearch,
  KeyRound,
  Lightbulb,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CSSProperties, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { Button } from "./ui/button";
import { procurementModeMenuItems } from "./procurementModeMenuItems";
import { StaffForegroundError } from "@/components/StaffForegroundError";
import { useForegroundPageFailure } from "@/hooks/useStaffShellForegroundLoading";
import { createIncidentId } from "@/lib/chunkLoadRecovery";
import { useQueryClient } from "@tanstack/react-query";

export { procurementModeMenuItems } from "./procurementModeMenuItems";

const CleoPortalChat = lazy(() => import("./CleoPortalChat"));

// —— Service mode: main + finance + admin (no Sales group — use “Switch to Sales” instead)

const mainMenuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/", roles: [] },
  { icon: Activity, label: "Activity", path: "/admin/activity", roles: ["admin"] },
  { icon: Users, label: "Customers", path: "/customers", roles: [] },
  { icon: FolderOpen, label: "Projects", path: "/projects", roles: ["admin", "finance", "sales"] },
  {
    icon: CalendarDays,
    label: "Team Calendar",
    path: "/calendar",
    roles: ["admin", "finance", "sales"],
  },
  {
    icon: CalendarDays,
    label: "Field Calendar",
    path: "/calendar/field",
    roles: ["service", "programming", "installer", "admin"],
  },
  { icon: IdCard, label: "My Digital Card", path: "/my-digital-card", roles: [] },
  { icon: ClipboardList, label: "Repair Orders", path: "/repair-orders", roles: [] },
  { icon: MapPin, label: "Service Visits", path: "/site-visits", roles: [] },
  { icon: ClipboardList, label: "My Tasks", path: "/my-tasks", roles: [] },
  { icon: CalendarCheck, label: "My Schedule", path: "/my-schedule", roles: ["service", "programming", "installer"] },
  { icon: Upload, label: "Delivery Orders", path: "/delivery-orders", roles: ["admin", "service"] },
  { icon: Package, label: "Products", path: "/products", roles: [] },
];

const serviceSandboxMenuItems = [
  { label: "Sandbox home", path: "/sales/sandbox", roles: ["admin"] as const },
  { label: "D-Tools Parser Test", path: "/sales/sandbox/dtools-parser", roles: ["admin"] as const },
  {
    label: "D-Tools JSON Upload",
    path: "/sales/sandbox/dtools-json-upload",
    roles: ["admin"] as const,
  },
  {
    label: "Proposal Intelligence Sandbox",
    path: "/sales/sandbox/proposal-intelligence",
    roles: ["admin"] as const,
  },
  {
    label: "Project Markup",
    path: "/sales/sandbox/project-markup",
    roles: ["admin"] as const,
  },
  {
    label: "Project Catalogue",
    path: "/sales/sandbox/project-catalogue",
    roles: ["admin"] as const,
  },
];

const financeMenuItems = [
  { icon: GitCompare, label: "Reconciliation", path: "/finance/reconciliation", roles: ["admin", "finance"] },
  { icon: BarChart3, label: "Revenue Calendar", path: "/finance/calendar", roles: ["admin", "finance"] },
  { icon: FileText, label: "Invoices", path: "/finance/invoices", roles: ["admin", "finance"] },
  { icon: Upload, label: "Legacy Invoice Import", path: "/finance/legacy-import", roles: ["admin", "finance"] },
  { icon: DollarSign, label: "Log Manual Entry", path: "/finance/revenue", roles: ["admin", "finance"] },
  { icon: TrendingUp, label: "Revenue Schedule", path: "/finance/schedule", roles: ["admin", "finance"] },
];

// Sales mode only — full replacement sidebar
const salesModeMenuItems = [
  { icon: LayoutDashboard, label: "Sales Dashboard", path: "/sales", roles: [] },
  { icon: Activity, label: "Activity", path: "/admin/activity", roles: ["admin"] },
  { icon: Store, label: "Catalogue", path: "/sales/catalogue", roles: ["sales", "admin", "finance"] },
  { icon: BarChart2, label: "My Insights", path: "/sales/insights", roles: [] },
  { icon: FolderOpen, label: "Projects", path: "/projects", roles: [] },
  { icon: Kanban, label: "Deal Projects", path: "/sales/projects", roles: [] },
  { icon: ClipboardList, label: "Proposals", path: "/sales/proposals", roles: [] },
  {
    icon: Upload,
    label: "D-Tools JSON Upload",
    path: "/sales/dtools-json-upload",
    roles: ["sales", "admin", "finance"],
  },
  { icon: FileText, label: "Invoices", path: "/sales/invoices", roles: [] },
  { icon: Users, label: "Customers", path: "/customers", roles: [] },
  { icon: BookOpen, label: "Customer Log", path: "/sales/customer-log", roles: [] },
  { icon: CalendarDays, label: "Appointments", path: "/sales/appointments", roles: [] },
  { icon: IdCard, label: "My Digital Card", path: "/my-digital-card", roles: [] },
  { icon: History, label: "Time Machine", path: "/sales/time-machine", roles: ["sales", "admin"] },
];

const salesSandboxMenuItems = [
  { label: "Sandbox home", path: "/sales/sandbox", roles: ["sales", "admin"] as const },
  { label: "D-Tools Parser Test", path: "/sales/sandbox/dtools-parser", roles: ["sales", "admin"] as const },
  {
    label: "D-Tools JSON Upload",
    path: "/sales/sandbox/dtools-json-upload",
    roles: ["sales", "admin"] as const,
  },
  {
    label: "Proposal Intelligence Sandbox",
    path: "/sales/sandbox/proposal-intelligence",
    roles: ["sales", "admin"] as const,
  },
  {
    label: "Sales Insights Sandbox",
    path: "/sales/sandbox/sales-insights",
    roles: ["sales", "admin"] as const,
  },
  {
    label: "Project Markup",
    path: "/sales/sandbox/project-markup",
    roles: ["sales", "admin"] as const,
  },
  {
    label: "Project Catalogue",
    path: "/sales/sandbox/project-catalogue",
    roles: ["sales", "admin"] as const,
  },
  {
    label: "Clarity Website",
    path: "/sandbox/clarity-website",
    roles: ["sales", "admin"] as const,
  },
];

const SANDBOX_MENU_PATHS = [
  "/sales/sandbox",
  "/sales/sandbox/dtools-parser",
  "/sales/sandbox/dtools-json-upload",
  "/sales/sandbox/proposal-intelligence",
  "/sales/sandbox/sales-insights",
  "/sales/sandbox/project-markup",
  "/sales/sandbox/project-catalogue",
  "/sandbox/clarity-website",
];

const adminMenuItems = [
  { icon: UserCog, label: "Staff & Users", path: "/admin", roles: ["admin"] },
  { icon: UserRoundSearch, label: "Customers", path: "/admin/customers", roles: ["admin"] },
  { icon: ClipboardList, label: "Headphone orders", path: "/admin/headphone-orders", roles: ["admin"] },
  { icon: Upload, label: "Upload Price List", path: "/admin/upload-price-list", roles: ["admin"] },
  { icon: Copy, label: "Duplicate Products", path: "/admin/duplicate-products", roles: ["admin"] },
  { icon: BookOpen, label: "Knowledge Centre", path: "/admin/articles", roles: ["admin", "marketing"] },
  { icon: BarChart3, label: "Reports & Analytics", path: "/admin/analytics", roles: ["admin", "finance"] },
  { icon: BarChart3, label: "Service Insights", path: "/admin/svr-analytics", roles: ["admin"] },
  { icon: Wrench, label: "RO & Servicing", path: "/admin/ro-servicing", roles: ["admin"] },
  { icon: Bell, label: "Notification policies", path: "/notifications", roles: ["admin"] },
  { icon: ScrollText, label: "Notification logs", path: "/admin/notification-logs", roles: ["admin"] },
  { icon: MessagesSquare, label: "Communication Logs", path: "/admin/customer-communication-logs", roles: ["admin", "finance"] },
  { icon: ClipboardList, label: "Issue Reports", path: "/admin/issues", roles: ["admin"] },
  { icon: MessageSquareWarning, label: "Cleo Operations Desk", path: "/cleo/operations", roles: [] },
  { icon: MessageSquareWarning, label: "Cleo mismatches", path: "/admin/cleo-mismatches", roles: ["admin"] },
  { icon: MessageSquareWarning, label: "Cleo unknown signals", path: "/admin/cleo-unknown-signals", roles: ["admin"] },
  { icon: Settings, label: "Settings", path: "/settings", roles: ["admin"] },
  { icon: IdCard, label: "Digital card (company)", path: "/admin/digital-card-company", roles: ["admin"] },
  { icon: Wallet, label: "Kaleidescape Dashboard", path: "/admin/kaleidescape", roles: ["admin", "finance", "sales"] },
  { icon: Truck, label: "Suppliers", path: "/suppliers", roles: ["admin", "finance"] },
  { icon: Activity, label: "Integration", path: "/admin/integration", roles: ["admin"] },
  { icon: KeyRound, label: "MCP connections", path: "/admin/mcp-connections", roles: ["admin"] },
  { icon: Lightbulb, label: "Coaching playbook", path: "/admin/coaching-playbook", roles: ["admin"] },
  { icon: ScrollText, label: "Changelog", path: "/changelog", roles: ["admin"] },
];

const ADMIN_PROPOSALS_MENU_PATHS = ["/admin/proposals/intro", "/admin/proposals/intro/preview", "/admin/sales-disclaimers"];

const adminProposalsMenuItems = [
  { label: "Intro", path: "/admin/proposals/intro", roles: ["admin"] as const },
  { label: "Disclaimers", path: "/admin/sales-disclaimers", roles: ["admin"] as const },
];

const MODULE_SWITCHER_ICON: Record<PortalModule, typeof BarChart2> = {
  sales: BarChart2,
  service: Wrench,
  procurement: Package,
};

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

// Empty `roles` means everyone; otherwise honour primary + secondary roles.
function filterByRole<T extends { roles: readonly string[] }>(
  items: T[],
  user: UserRoleFields | null | undefined,
) {
  return items.filter((item) => item.roles.length === 0 || hasRoleCapability(user, item.roles));
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user, logout } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.href = getLoginUrl();
    }
    return <DashboardLayoutSkeleton />;
  }

  if (isHrsTesterEmail(user.email)) {
    return (
      <div className="min-h-screen bg-muted/30">
        <header className="border-b bg-background">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3">
            <div className="flex items-center gap-4">
              <img src={ClarityLogoDark} alt="Clarity" className="h-8 w-auto" />
              <div className="border-l pl-4">
                <p className="text-sm font-semibold">HRS Rack Configurator</p>
                <p className="text-xs text-muted-foreground">External test access</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden text-xs text-muted-foreground sm:inline">{user.email}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  await logout();
                  window.location.href = getLoginUrl();
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>
        </header>
        <main className="px-5 py-6">{children}</main>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>{children}</DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({ children, setSidebarWidth }: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const pageFailure = useForegroundPageFailure();
  const [incidentId, setIncidentId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (pageFailure && !incidentId) setIncidentId(createIncidentId());
    if (!pageFailure) setIncidentId(null);
  }, [pageFailure, incidentId]);
  const { module, isSales, isProcurement, enterSales, enterService, enterProcurement } = usePortalMode();
  const moduleAccess = useMemo(() => portalModuleAccessForUser(user), [user]);
  const accessibleModules = useMemo(
    () => PORTAL_MODULES.filter((m) => moduleAccess[m]),
    [moduleAccess],
  );
  const canSeeFinanceAdmin = hasRoleCapability(user, ["admin", "finance"]);
  const enterModule = useCallback(
    (m: PortalModule) => {
      if (m === "sales") enterSales();
      else if (m === "procurement") enterProcurement();
      else enterService();
    },
    [enterSales, enterService, enterProcurement],
  );
  trpc.financeTasks.pendingCount.useQuery(undefined, {
    enabled: canSeeFinanceAdmin,
    refetchInterval: 15000,
    meta: { background: true },
  });
  const { data: reconBadge } = trpc.financeInvoices.unresolvedPaymentReconciliationCount.useQuery(
    undefined,
    { enabled: canSeeFinanceAdmin, refetchInterval: 30000, meta: { background: true } },
  );
  const unresolvedReconCount = Number(reconBadge?.count ?? 0);
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [adminOpen, setAdminOpen] = useState(
    () => getActiveMenuPath(location, adminMenuItems.map((i) => i.path)) != null
  );
  const [financeOpen, setFinanceOpen] = useState(
    () => getActiveMenuPath(location, financeMenuItems.map((i) => i.path)) != null
  );
  const [sandboxOpen, setSandboxOpen] = useState(
    () => getActiveMenuPath(location, SANDBOX_MENU_PATHS) != null
  );
  const [proposalsOpen, setProposalsOpen] = useState(
    () => getActiveMenuPath(location, ADMIN_PROPOSALS_MENU_PATHS) != null
  );
  const isMobile = useIsMobile();
  const daylightAmbience = useDaylightModeOptional();
  const salesAmbienceActive = Boolean(isSales && daylightAmbience?.activeOnPage);
  const clarityLogoSrc = !isSales
    ? ClarityLogoLight
    : isSales && salesAmbienceActive && daylightAmbience?.band
      ? salesAmbienceUsesLightLogo(daylightAmbience.band)
        ? ClarityLogoLight
        : ClarityLogoDark
      : ClarityLogoDark;

  const mainFiltered = useMemo(() => filterByRole(mainMenuItems, user), [user]);
  const financeFiltered = useMemo(() => filterByRole(financeMenuItems, user), [user]);
  const adminFiltered = useMemo(() => filterByRole(adminMenuItems, user), [user]);
  const adminProposalsFiltered = useMemo(() => filterByRole(adminProposalsMenuItems, user), [user]);
  const salesFiltered = useMemo(() => filterByRole(salesModeMenuItems, user), [user]);
  const salesSandboxFiltered = useMemo(() => filterByRole(salesSandboxMenuItems, user), [user]);
  const serviceSandboxFiltered = useMemo(() => filterByRole(serviceSandboxMenuItems, user), [user]);
  const procurementFiltered = useMemo(() => filterByRole(procurementModeMenuItems, user), [user]);
  const sandboxFiltered = isSales ? salesSandboxFiltered : serviceSandboxFiltered;
  const showClubNav = showClubBookingNav(user);
  const clubNavActive = isClubBookingNavActive(location);

  const mainPaths = useMemo(() => mainFiltered.map((i) => i.path), [mainFiltered]);
  const financePaths = useMemo(() => financeFiltered.map((i) => i.path), [financeFiltered]);
  const procurementPaths = useMemo(() => procurementFiltered.map((i) => i.path), [procurementFiltered]);
  const adminPaths = useMemo(
    () => [...adminFiltered.map((i) => i.path), ...adminProposalsFiltered.map((i) => i.path)],
    [adminFiltered, adminProposalsFiltered],
  );
  const proposalsPaths = useMemo(() => adminProposalsFiltered.map((i) => i.path), [adminProposalsFiltered]);
  const salesPaths = useMemo(() => salesFiltered.map((i) => i.path), [salesFiltered]);
  const sandboxPaths = useMemo(() => sandboxFiltered.map((i) => i.path), [sandboxFiltered]);

  const activeMenuItem = useMemo(() => {
    if (showClubNav && clubNavActive) return CLUB_BOOKING_NAV_ITEM;
    if (isProcurement) {
      const best = getActiveMenuPath(location, [...procurementPaths, ...adminPaths]);
      const all = [...procurementFiltered, ...adminFiltered, ...adminProposalsFiltered];
      return all.find((i) => i.path === best) ?? null;
    }
    if (isSales) {
      const best = getActiveMenuPath(location, [...salesPaths, ...sandboxPaths, ...adminPaths]);
      const all = [...salesFiltered, ...sandboxFiltered, ...adminFiltered, ...adminProposalsFiltered];
      return all.find((i) => i.path === best) ?? null;
    }
    const best = getActiveMenuPath(location, [...mainPaths, ...financePaths, ...sandboxPaths, ...adminPaths]);
    const all = [...mainFiltered, ...financeFiltered, ...sandboxFiltered, ...adminFiltered, ...adminProposalsFiltered];
    return all.find((i) => i.path === best) ?? null;
  }, [isProcurement, isSales, location, mainFiltered, financeFiltered, adminFiltered, adminProposalsFiltered, salesFiltered, procurementFiltered, mainPaths, financePaths, adminPaths, salesPaths, procurementPaths, sandboxPaths, showClubNav, clubNavActive]);

  const renderClubNavItem = (accent: "sales" | "default") => {
    if (!showClubNav) return null;
    const iconActive = accent === "sales" ? "text-sidebar-primary" : "text-primary";
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={clubNavActive}
          onClick={() => setLocation(CLUB_BOOKING_NAV_ITEM.path)}
          tooltip={CLUB_BOOKING_NAV_ITEM.label}
          className="h-10 transition-all font-normal"
        >
          <CLUB_BOOKING_NAV_ITEM.icon className={cn("h-4 w-4", clubNavActive ? iconActive : "")} />
          <span>{CLUB_BOOKING_NAV_ITEM.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const renderAdminProposalsNested = (childIndent: string) => {
    if (adminProposalsFiltered.length === 0) return null;
    return (
      <Collapsible open={proposalsOpen} onOpenChange={setProposalsOpen}>
        <CollapsibleTrigger asChild>
          <SidebarMenuItem>
            <SidebarMenuButton className={cn("h-9 pl-6 text-sm text-sidebar-foreground/80", childIndent)} tooltip="Proposals">
              <FileText className="h-4 w-4" />
              <span className="font-medium">Proposals</span>
              <span className="ml-auto">
                {proposalsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-8 border-l border-border/50 pl-3">
            {adminProposalsFiltered.map((item) => {
              const isActive = isItemActiveInSet(item.path, location, proposalsPaths);
              return (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    isActive={isActive}
                    onClick={() => setLocation(item.path)}
                    tooltip={item.label}
                    className="h-9 text-sm"
                  >
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className={cn("border-r-0 transition-[color,box-shadow] duration-300", isSales && "shadow-sm")}
          disableTransition={isResizing}
        >
          <SidebarHeader
            className={cn(
              "gap-0 transition-colors duration-300",
              isCollapsed
                ? "justify-center px-2 py-2"
                : "p-0 px-2 pt-4 sm:pt-5 pb-2"
            )}
          >
            <div
              className={cn(
                "flex w-full min-w-0 items-center gap-3 transition-all",
                !isCollapsed && "mb-4 sm:mb-5"
              )}
            >
              <button
                onClick={toggleSidebar}
                className={cn(
                  "h-8 w-8 flex shrink-0 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isSales ? "hover:bg-white/10" : "hover:bg-accent"
                )}
                aria-label="Toggle navigation"
                type="button"
              >
                <PanelLeft className={cn("h-4 w-4", isSales ? "text-slate-300" : "text-muted-foreground")} />
              </button>
              {!isCollapsed ? (
                <div className="flex min-w-0 flex-1 items-center">
                  <img src={clarityLogoSrc} alt="Clarity" className="clarity-logo" />
                </div>
              ) : null}
            </div>
            {!isCollapsed && accessibleModules.length > 1 && (
              <div className="w-full">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-9 w-full justify-between text-xs font-medium transition-all duration-300",
                        isSales
                          ? "border-white/20 bg-white/5 text-slate-100 hover:bg-white/10 hover:text-white"
                          : "border-border bg-background/40 hover:bg-accent"
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {(() => {
                          const Icon = MODULE_SWITCHER_ICON[module];
                          return <Icon className="h-3.5 w-3.5" />;
                        })()}
                        {PORTAL_MODULE_LABELS[module]}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-52">
                    {accessibleModules.map((m) => {
                      const Icon = MODULE_SWITCHER_ICON[m];
                      return (
                        <DropdownMenuItem key={m} onClick={() => enterModule(m)} className="cursor-pointer">
                          <Icon className="mr-2 h-4 w-4" />
                          <span>{PORTAL_MODULE_LABELS[m]}</span>
                          {m === module && <Check className="ml-auto h-3.5 w-3.5" />}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            {isCollapsed && accessibleModules.length > 1 && (
              <div className="flex justify-center px-1">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton isActive={false} tooltip="Switch module" className="h-9">
                          {(() => {
                            const Icon = MODULE_SWITCHER_ICON[module];
                            return <Icon className="h-4 w-4" />;
                          })()}
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" side="right" className="min-w-52">
                        {accessibleModules.map((m) => {
                          const Icon = MODULE_SWITCHER_ICON[m];
                          return (
                            <DropdownMenuItem key={m} onClick={() => enterModule(m)} className="cursor-pointer">
                              <Icon className="mr-2 h-4 w-4" />
                              <span>{PORTAL_MODULE_LABELS[m]}</span>
                              {m === module && <Check className="ml-auto h-3.5 w-3.5" />}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                </SidebarMenu>
              </div>
            )}
          </SidebarHeader>

          <SidebarContent className="gap-0">
            {isProcurement ? (
              <SidebarMenu className="px-2 py-1">
                <div className="px-2 pb-1.5 pt-0.5 text-xs font-semibold tracking-wider text-sidebar-foreground/55">
                  PROCUREMENT &amp; INVENTORY
                </div>
                {procurementFiltered.map((item) => {
                  const isActive = isItemActiveInSet(item.path, location, procurementPaths);
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => setLocation(item.path)}
                        tooltip={item.label}
                        className="h-10 transition-all font-normal"
                      >
                        <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                {renderClubNavItem("default")}

                {canSeeFinanceAdmin && !isCollapsed && (
                  <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-muted-foreground"
                          tooltip="Admin"
                        >
                          <Settings className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Admin</span>
                          <span className="ml-auto">
                            {adminOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {renderAdminProposalsNested("")}
                      {adminFiltered.map((item) => {
                        const isActive = isItemActiveInSet(item.path, location, adminPaths);
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              isActive={isActive}
                              onClick={() => setLocation(item.path)}
                              tooltip={item.label}
                              className="h-9 pl-6"
                            >
                              <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {canSeeFinanceAdmin &&
                  isCollapsed &&
                  adminFiltered.map((item) => {
                    const isActive = isItemActiveInSet(item.path, location, adminPaths);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => setLocation(item.path)}
                          tooltip={item.label}
                          className="h-9"
                        >
                          <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            ) : isSales ? (
              <SidebarMenu className="px-2 py-1">
                <div className="px-2 pb-1.5 pt-0.5 text-xs font-semibold tracking-wider text-sidebar-foreground/55">
                  SALES
                </div>
                {salesFiltered.map((item) => {
                  const isActive = isItemActiveInSet(item.path, location, salesPaths);
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => setLocation(item.path)}
                        tooltip={item.label}
                        className="h-10 transition-all font-normal"
                      >
                        <item.icon className={cn("h-4 w-4", isActive && "text-sidebar-primary")} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                {renderClubNavItem("sales")}

                {salesSandboxFiltered.length > 0 && !isCollapsed && (
                  <Collapsible open={sandboxOpen} onOpenChange={setSandboxOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-sidebar-foreground/60"
                          tooltip="Sandbox"
                        >
                          <FlaskConical className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Sandbox</span>
                          <span className="ml-auto">
                            {sandboxOpen ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-3 border-l border-border/50 ml-4 mt-0.5 space-y-0.5">
                        {salesSandboxFiltered.map((item) => {
                          const isActive = isItemActiveInSet(item.path, location, sandboxPaths);
                          return (
                            <SidebarMenuItem key={item.path}>
                              <SidebarMenuButton
                                isActive={isActive}
                                onClick={() => setLocation(item.path)}
                                tooltip={item.label}
                                className="h-9 text-sm"
                              >
                                <span>{item.label}</span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {canSeeFinanceAdmin && !isCollapsed && (
                  <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-sidebar-foreground/60"
                          tooltip="Admin"
                        >
                          <Settings className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Admin</span>
                          <span className="ml-auto">
                            {adminOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {renderAdminProposalsNested("")}
                      {adminFiltered.map((item) => {
                        const isActive = isItemActiveInSet(item.path, location, adminPaths);
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              isActive={isActive}
                              onClick={() => setLocation(item.path)}
                              tooltip={item.label}
                              className="h-9 pl-6 text-sm"
                            >
                              <item.icon className={cn("h-4 w-4", isActive && "text-sidebar-primary")} />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {(canSeeFinanceAdmin) &&
                  isCollapsed &&
                  adminFiltered.map((item) => {
                    const isActive = isItemActiveInSet(item.path, location, adminPaths);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => setLocation(item.path)}
                          tooltip={item.label}
                          className="h-9"
                        >
                          <item.icon className={cn("h-4 w-4", isActive && "text-sidebar-primary")} />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            ) : (
              <SidebarMenu className="px-2 py-1">
                {mainFiltered.map((item) => {
                  const isActive = isItemActiveInSet(item.path, location, mainPaths);
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => setLocation(item.path)}
                        tooltip={item.label}
                        className="h-10 transition-all font-normal"
                      >
                        <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                {renderClubNavItem("default")}

                {serviceSandboxFiltered.length > 0 && !isCollapsed && (
                  <Collapsible open={sandboxOpen} onOpenChange={setSandboxOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-muted-foreground"
                          tooltip="Sandbox"
                        >
                          <FlaskConical className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Sandbox</span>
                          <span className="ml-auto">
                            {sandboxOpen ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-3 border-l border-border/50 ml-4 mt-0.5 space-y-0.5">
                        {serviceSandboxFiltered.map((item) => {
                          const isActive = isItemActiveInSet(item.path, location, sandboxPaths);
                          return (
                            <SidebarMenuItem key={item.path}>
                              <SidebarMenuButton
                                isActive={isActive}
                                onClick={() => setLocation(item.path)}
                                tooltip={item.label}
                                className="h-9 text-sm"
                              >
                                <span>{item.label}</span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {(canSeeFinanceAdmin) && !isCollapsed && (
                  <Collapsible open={financeOpen} onOpenChange={setFinanceOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-muted-foreground"
                          tooltip="Finance"
                        >
                          <DollarSign className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Finance</span>
                          <span className="ml-auto">
                            {financeOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-3 border-l border-border/50 ml-4 mt-0.5 space-y-0.5">
                        {financeFiltered.map((item) => {
                          const isActive = isItemActiveInSet(item.path, location, financePaths);
                          return (
                            <SidebarMenuItem key={item.path}>
                              <SidebarMenuButton
                                isActive={isActive}
                                onClick={() => setLocation(item.path)}
                                tooltip={item.label}
                                className="h-9 text-sm"
                              >
                                <item.icon className={cn("h-3.5 w-3.5", isActive ? "text-primary" : "")} />
                                <span>{item.label}</span>
                                {item.path === "/finance/invoices" && unresolvedReconCount > 0 ? (
                                  <span className="ml-auto rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                    {unresolvedReconCount > 99 ? "99+" : unresolvedReconCount}
                                  </span>
                                ) : null}
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {(canSeeFinanceAdmin) && !isCollapsed && (
                  <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className="h-10 transition-all font-normal mt-1 text-muted-foreground"
                          tooltip="Admin"
                        >
                          <Settings className="h-4 w-4" />
                          <span className="font-semibold tracking-wide text-xs uppercase">Admin</span>
                          <span className="ml-auto">
                            {adminOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {renderAdminProposalsNested("")}
                      {adminFiltered.map((item) => {
                        const isActive = isItemActiveInSet(item.path, location, adminPaths);
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              isActive={isActive}
                              onClick={() => setLocation(item.path)}
                              tooltip={item.label}
                              className="h-9 pl-6"
                            >
                              <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {(canSeeFinanceAdmin) && isCollapsed && (
                  <>
                    {financeFiltered.map((item) => {
                      const isActive = isItemActiveInSet(item.path, location, financePaths);
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            isActive={isActive}
                            onClick={() => setLocation(item.path)}
                            tooltip={item.label}
                            className="h-9"
                          >
                            <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                    {adminFiltered.map((item) => {
                      const isActive = isItemActiveInSet(item.path, location, adminPaths);
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            isActive={isActive}
                            onClick={() => setLocation(item.path)}
                            tooltip={item.label}
                            className="h-9"
                          >
                            <item.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </>
                )}
              </SidebarMenu>
            )}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "group-data-[collapsible=icon]:justify-center flex w-full items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isSales ? "hover:bg-white/5" : "hover:bg-accent/50"
                  )}
                  type="button"
                >
                  <Avatar className="h-9 w-9 shrink-0 border">
                    <AvatarFallback className="text-xs font-medium">{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                    <p className="truncate text-sm font-medium leading-none">{user?.name || "—"}</p>
                    <p
                      className={cn(
                        "mt-1.5 truncate text-xs",
                        isSales ? "text-slate-400" : "text-muted-foreground"
                      )}
                    >
                      {user?.email || "—"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => setLocation("/my-preferences")}
                  className="cursor-pointer"
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  <span>My Preferences</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={cn(
            "absolute top-0 right-0 z-50 h-full w-1 cursor-col-resize transition-colors",
            isCollapsed && "hidden",
            isSales ? "hover:bg-[#C8A96A]/25" : "hover:bg-primary/20"
          )}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          aria-hidden
        />
      </div>

      <SidebarInset
        key={module}
        className="bg-background transition-[background-color,color] duration-300 ease-out"
      >
        <div className="portal-mode-inset-anim flex w-full min-h-0 flex-1 flex-col">
          {isMobile && (
            <div className="bg-background/95 supports-[backdrop-filter]:bg-background/80 supports-[backdrop-filter]:backdrop-blur border-b h-14 sticky top-0 z-40 flex items-center justify-between px-2">
              <div className="flex items-center gap-2 min-w-0">
                <SidebarTrigger className="h-9 w-9 shrink-0 rounded-lg bg-background" />
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-foreground text-sm font-medium tracking-tight truncate">
                      {isSales ? (activeMenuItem?.label ?? "Sales") : (activeMenuItem?.label ?? "Menu")}
                    </span>
                    {isSales && <span className="text-xs text-muted-foreground">Sales mode</span>}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 shrink-0 px-2 text-xs"
                onClick={() => setLocation("/my-preferences")}
              >
                <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
                Preferences
              </Button>
            </div>
          )}
          <main className="flex-1 px-4 pt-4 pb-24">
            {pageFailure && incidentId ? (
              <StaffForegroundError
                route={location}
                operation={pageFailure.operation}
                error={pageFailure.error}
                incidentId={incidentId}
                onRetry={() => {
                  void queryClient.refetchQueries({ type: "active" });
                }}
              />
            ) : null}
            <div className={pageFailure ? "hidden" : undefined}>{children}</div>
          </main>
        </div>
      </SidebarInset>
      <Suspense fallback={null}>
        <CleoPortalChat />
      </Suspense>
      <FloatingRoWorkTimers />
    </>
  );
}
